export const translations = {
  en: {
    title: "OnlyGrades",
    message: {
      home: "Home",
      premiumNav: "Premium",
      advancedNav: "Advanced",
      privacyNav: "Privacy",
      termsNav: "Terms",
      about: "About",
      easiestHardestCourses: "Easiest/Hardest Courses",
      accountMenu: "Account",
      logout: "Log out",
      aboutTitle: "About OnlyGrades",
      aboutDescription1:
        "OnlyGrades is a comprehensive platform designed to help students find detailed grade statistics for their courses. Our mission is to provide valuable insights into course performance, helping students make informed decisions about their education.",
      aboutDescription2:
        "Whether you are looking for the easiest courses, detailed grade distributions, or historical performance data, OnlyGrades has you covered.",
      aboutDescription3:
        "If you have any questions or need further assistance, please let us know at {{email}}.",
      aboutDescription3NoEmail:
        "If you have any questions or need further assistance, please check back soon for contact details.",
      aboutDataTitle: "Data and licensing",
      aboutDataDescription1:
        "OnlyGrades uses aggregated statistics from HK-dir's DBH service (Database for statistikk om høyere utdanning).",
      aboutDataDescription2:
        "DBH API data is published under NLOD 2.0, which allows commercial use when attribution and license conditions are followed.",
      aboutDataAttributionLabel: "Recommended attribution:",
      aboutDataAttributionQuote:
        "Data from Database for statistikk om høyere utdanning (DBH), licensed under NLOD 2.0.",
      aboutDataRequirement1:
        "Source attribution: DBH/HK-dir and NLOD should be referenced where data is shown or exported.",
      aboutDataRequirement2:
        "Data integrity: results must not be presented in a misleading or distorted way.",
      aboutDataRequirement3:
        "Personal data: only aggregated/open data is in scope; personal data requires separate legal basis.",
      aboutDataSourcesTitle: "Official sources",
      aboutLinkDbhDocs: "DBH API documentation (PDF)",
      aboutLinkDataset: "DBH API dataset listing (data.norge.no)",
      aboutLinkNlod: "NLOD 2.0 license text",
      aboutDataVerified: "Licensing position last verified: {{date}}.",
      maintenance: {
        title: "Under construction",
        description:
          "OnlyGrades is temporarily unavailable while the backend is being rebuilt.",
        contact: "Contact: {{email}}",
      },
      homeWelcome: "Welcome to OnlyGrades",
      homeSubtitle: "Find detailed grade statistics for your courses.",
      chartHistorical: "Historical View",
      chartDistribution: "Grade Distribution",
      chartFailRates: "Historical Fail Rates",
      excludeFails: "Exclude Fails from Average",
      includeFails: "Include Fails in Average",
      selectAverageOption: "Select Average Grade Calculation",
      showFemaleStats: "Show Female Stats",
      hideFemaleStats: "Hide Female Stats",
      womenStats: "Women",
      semesters: {
        winter: "Winter",
        spring: "Spring",
        summer: "Summer",
        fall: "Fall",
      },
      search: {
        placeholder: "Search courses...",
        searchInstitution: "Search institutions...",
        searchStudyProgram: "Search study programs...",
        loadingCourses: "Loading courses...",
      },
      easiestHardestTitle: "Easiest and Hardest Courses",
      easiestCourses: "Easiest Courses",
      hardestCourses: "Hardest Courses",
      allRightsReserved: "All Rights Reserved",
      dataAttribution:
        "Contains data under NLOD from HK-dir via Database for statistikk om høyere utdanning (DBH).",
      cookies: {
        bannerText:
          "We use necessary storage for login/session and optional analytics only after consent.",
        learnMore: "Privacy details",
        reject: "Essential only",
        accept: "Accept analytics",
        settings: "Cookie settings",
      },
      api: {
        loadingCourses: "Loading course catalog...",
        loadingCourseData: "Loading chart data...",
        backendWakingUp:
          "Backend is waking up. This can take a short moment after inactivity.",
        retry: "Try again",
        catalogUnavailable:
          "Course catalog is temporarily unavailable while the backend starts.",
        catalogLoadFailed: "Could not load the course catalog right now.",
        courseUnavailable: "Could not load this course yet. Please retry shortly.",
        courseNotFound: "Could not find data for {{code}}.",
      },
      dashboard: {
        activeCharts: "Charts: {{count}} / {{max}}",
        tierFree: "Free dashboard",
        tierPremium: "Premium dashboard",
        searchToAdd: "Search and add courses to compare side-by-side.",
        compareBuilderTitle: "Compare in dashboard",
        compareBuilderBody:
          "Pick multiple courses here, then open them directly in the analysis dashboard.",
        addTopEasiest: "Add top 3 easiest",
        addTopHardest: "Add top 3 hardest",
        openSelected: "Open selected ({{count}})",
        clearCourses: "Clear dashboard",
        clearSelected: "Clear selected",
        selectedCount: "Selected courses: {{count}}",
        addCourse: "Add",
        addToStudyPlan: "Add to study plan",
        studyPlanSavedLabel: "In study plan",
        studyPlanRemoveLabel: "Remove from plan",
        studyPlanCount: "Study plan: {{count}}",
        studyPlanSaved: "{{code}} added to your study plan.",
        studyPlanSavedDev:
          "{{code}} added to your study plan (local dev mode).",
        studyPlanRemoved: "{{code}} removed from your study plan.",
        studyPlanRemovedDev:
          "{{code}} removed from your study plan (local dev mode).",
        studyPlanAlreadySaved: "{{code}} is already in your study plan.",
        studyPlanLoginRequired:
          "Sign in on Premium to save courses to your study plan.",
        studyPlanUnavailable:
          "Study plan is currently unavailable.",
        studyPlanFailed: "Could not update your study plan right now.",
        inDashboardPickerTitle: "Premium course picker (add directly to dashboard)",
        toolsTitle: "Dashboard tools",
        toolsSubtitle: "Adjust analysis options, chart style, and export.",
        expandTools: "Show tools",
        collapseTools: "Hide tools",
        summarySmartPicks: "Smart picks: {{count}} / {{total}}",
        summarySmartPicksHelp:
          "Courses in your current dashboard that score as low risk and high reward.",
        summaryStability: "Avg stability",
        summaryStabilityHelp:
          "How predictable outcomes are across recent terms. Higher is more stable.",
        summaryOfferProbability: "Avg offer probability",
        summaryOfferProbabilityHelp:
          "Estimated chance these courses are offered in the relevant semester based on recent years.",
        riskAxisLabel: "Risk score (lower is better)",
        rewardAxisLabel: "Reward score (higher is better)",
        riskRewardMapSubtitle:
          "Each point is one course, scored on a 0-100 risk/reward scale.",
        riskRewardMapClickHint:
          "Click a course point to jump directly to its chart below.",
        riskRewardMethodTitle: "How this is calculated",
        riskRewardMethodReward:
          "Reward = grade level (84%) + stability (16%).",
        riskRewardMethodRisk:
          "Risk = low pass rate (63%) + low offering probability (22%) + instability (15%).",
        riskRewardMethodQuadrant:
          "Smart picks are courses with risk <= 40 and reward >= 60.",
        toolsSectionView: "View",
        toolsSectionStyle: "Style",
        toolsSectionAdvanced: "Advanced",
        toolsSectionExport: "Export",
        viewOptions: "Analysis options",
        filterBothSemestersOn: "Only spring+fall courses",
        filterBothSemestersOff: "Show all offering patterns",
        filterBothSemestersHelp:
          "Filter charts and insights to courses historically offered in both spring and fall.",
        programFocusTitle: "Institution/program filter",
        programFocusHelp:
          "Choose institution first, then optionally a study program. Charts without matching data are hidden.",
        programFocusInstitutionAll: "All institutions",
        programFocusStudyProgramAll: "All study programs",
        programFocusHiddenCount: "{{count}} charts are hidden by this filter.",
        programFocusEmpty:
          "No open charts match this institution/program filter.",
        filterBothSemestersHiddenCount:
          "{{count}} courses are hidden by this filter.",
        filterBothSemestersEmpty:
          "No open courses match this filter. Turn it off to view all courses.",
        bulkChartMode: "Apply chart mode to all open courses",
        setAllHistorical: "All historical",
        setAllDistribution: "All distribution",
        setAllFailRates: "All fail rates",
        visualPalette: "Chart palette",
        paletteOnlygrades: "OnlyGrades",
        paletteSlate: "Slate",
        paletteWarm: "Warm",
        advancedTitle: "Advanced signals",
        advancedSubtitle:
          "Compact ranking from current dashboard courses (risk/reward, availability, stability).",
        advancedPremiumLocked:
          "Advanced dashboard signals are premium.",
        noAdvancedSignals: "Add more courses to generate advanced signals.",
        riskRewardMapTitle: "Risk/Reward map",
        riskLow: "Low risk",
        riskHigh: "High risk",
        rewardLow: "Low reward",
        rewardHigh: "High reward",
        signalStability: "Stability",
        signalOfferedBothSemesters: "Offered in both spring and fall",
        exportTitle: "Export",
        exportSubtitle: "Export current dashboard data as CSV or JSON.",
        exportCsv: "Export CSV",
        exportJson: "Export JSON",
        exportLocked: "Export is a premium feature.",
        exportReadyCsv: "CSV export is ready.",
        exportReadyJson: "JSON export is ready.",
        saveDashboard: "Save dashboard",
        loadDashboard: "Load saved",
        dashboardSaved: "Dashboard saved.",
        dashboardLoaded: "Saved dashboard loaded.",
        noSavedDashboard: "No saved dashboard found.",
        dashboardLoadFailed: "Could not load saved dashboard.",
        removeCourse: "Remove course",
        removeCourseShort: "Remove",
        duplicateCourse: "Course already in dashboard. Refreshed with latest data.",
        fetchFailed: "Could not load this course right now.",
        noData: "No chart data available for this selection.",
        studyProgramFilter: "Study program",
        studyProgramAll: "All study programs",
        studyPlanShortAdd: "Plan +",
        studyPlanShortRemove: "Plan -",
        chartType: "Chart type",
        limitReached: "You have reached the dashboard limit ({{max}} courses).",
        premiumLocked:
          "You can compare 2 courses for free. Unlock premium for 3-8 courses and advanced dashboard features.",
        unlockPremium: "Unlock premium",
      },
      account: {
        title: "My account",
        subtitle:
          "Manage your login, saved study plan, and privacy tools in one place.",
        loginPrompt:
          "Sign in with Google to access your saved study plan and account tools.",
        devMockHint:
          "Dev mode: study plan is stored locally on this browser when you are not signed in.",
        devSignInFree: "Dev sign-in (free)",
        devSignInPremium: "Dev sign-in (premium)",
        devMockSession: "Dev mock session",
        devSwitchToFree: "Switch to free",
        devSwitchToPremium: "Switch to premium",
        devSignedIn: "Signed in with local dev mock account.",
        devMockNoServer:
          "This is a local mock session. Data export/delete requires real login + backend auth.",
        unavailable:
          "Account features are currently unavailable.",
      },
      advanced: {
        title: "Advanced Analytics (Beta)",
        subtitle:
          "Plain-language analysis ideas we can ship with current data.",
        devNote:
          "This tab is for development planning. Metrics here are based on data we already compute.",
        controlsTitle: "Insights controls",
        generateInsights: "Generate insights",
        mapTitle: "Risk/Reward map",
        shortlistTitle: "Next semester shortlist",
        noInsightsYet: "No insights yet. Pick institution/program and generate.",
        noShortlistYet: "No shortlist yet. Generate insights first.",
        insightsFailed: "Failed to generate insights.",
        highValueTitle: "High-value now",
        nextLevelTitle: "Next level later",
        metrics: {
          risk: "Risk",
          reward: "Reward",
          passRate: "Pass rate",
          offerProbability: "Offer probability",
        },
        quadrants: {
          smart_picks: "Smart picks",
          safe_choices: "Safe choices",
          high_risk_high_reward: "High risk / high reward",
          review_carefully: "Review carefully",
        },
        reasonLabels: {
          high_pass_rate: "High pass rate",
          stable: "Stable outcomes",
          offered_often: "Offered often",
          offered_both_semesters: "Runs in both semesters",
          high_confidence: "High confidence",
          balanced_profile: "Balanced profile",
        },
        items: {
          riskReward: {
            title: "Risk/Reward map",
            userText:
              "Find courses that are easier than expected without taking too much risk.",
            dataText:
              "Combines adjusted difficulty, pass rate, offer probability, and stability (SEM).",
          },
          confidenceRanking: {
            title: "Confidence-adjusted ranking",
            userText:
              "See which rankings are solid and which are uncertain.",
            dataText:
              "Shows conservative score using lower confidence bound (Adjusted_Difficulty_Lower_CL).",
          },
          trendArrows: {
            title: "Trend arrows",
            userText:
              "Quickly see if a course is getting easier, harder, safer, or riskier over time.",
            dataText:
              "Uses recent slope of average grade and pass rate across latest offerings.",
          },
          stabilityScore: {
            title: "Stability score",
            userText: "Prefer courses with more predictable outcomes.",
            dataText:
              "Penalizes high variance (SEM) and low historical sample size.",
          },
          offerProbability: {
            title: "Offer probability",
            userText:
              "Avoid building plans around courses that rarely run.",
            dataText:
              "Based on how often the course appears in the same semester over recent years.",
          },
          genderGap: {
            title: "Gender gap panel",
            userText:
              "See where women and the total cohort perform differently.",
            dataText:
              "Compares average_grade_women_no_f and pass_rate_women against cohort aggregates.",
          },
          anomalyAlerts: {
            title: "Anomaly alerts",
            userText:
              "Highlight courses that suddenly changed difficulty or fail rates.",
            dataText:
              "Detects deviations from each course's own historical baseline.",
          },
          programHeatmap: {
            title: "Program heatmap",
            userText: "Spot heavy semesters at a glance.",
            dataText:
              "Year-semester heatmap of difficulty and fail-rate signals by program.",
          },
          paretoCourses: {
            title: "Pareto shortlist",
            userText:
              "Show courses with the best balance of easier + stable + likely offered.",
            dataText:
              "Multi-objective filter using difficulty, stability, and offer probability.",
          },
          personalShortlist: {
            title: "Personal shortlist",
            userText:
              "Generate a cleaner recommendation list from your passed courses and risk preference.",
            dataText:
              "Extends current planner scoring with confidence and availability constraints.",
          },
        },
      },
      premium: {
        title: "Premium Planner",
        subtitle:
          "Build a personalized shortlist using your study program, completed courses, and risk preference.",
        disclaimer:
          "Recommendations are statistical estimates, not official study guidance or guaranteed outcomes.",
        whatIncludedTitle: "What Premium includes",
        whatIncludedBody:
          "Premium is built for students who want faster, better decisions across multiple course options.",
        highlightPersonalized: "Personalized planning from your own course history",
        highlightCompare: "Compare more courses side by side in one dashboard",
        highlightRiskMap: "Risk/Reward map and advanced decision signals",
        highlightStudyPlan: "Saved study plan synced to your account",
        freeTierTitle: "Free",
        freeTierPrice: "0 kr/month",
        premiumTierTitle: "Premium",
        premiumTierPrice: "Paid plan",
        freeFeatureSearch: "Search and inspect historical grade data",
        freeFeatureCompare2: "Compare up to 2 courses in dashboard",
        freeFeatureBasicLists: "University-level easiest/hardest lists",
        freeFeatureHistoryCharts: "Core trend, distribution, and fail-rate charts",
        premiumFeatureCompare8: "Compare up to 8 courses in one dashboard",
        premiumFeatureAdvancedSignals:
          "Advanced signals: stability, offer probability, risk/reward map",
        premiumFeaturePersonalPlanner:
          "Program-level personalized recommendations",
        premiumFeatureStudyPlan: "Account-based study plan saving",
        premiumFeatureExport: "Export and account data self-service tools",
        premiumFeaturePriority: "Early access to new premium analytics",
        entryCta: "See Premium",
        entryCopy: "See what Premium includes and unlock when you are ready.",
        signInToUnlock: "Sign in to unlock Premium",
        signInRequiredCheckout: "Sign in first to start checkout.",
        checkoutUnavailable:
          "Premium checkout is not enabled right now. Join the early signup list.",
        openPlanner: "Open planner",
        loginPrompt: "Sign in with Google to save completed courses and unlock premium planning.",
        planStatus: "Plan status",
        refreshStatus: "Refresh status",
        logout: "Log out",
        accountToolsTitle: "Privacy tools",
        exportData: "Download my data",
        deleteAccount: "Delete my account",
        exportSuccess: "Your data export is ready.",
        exportFailed: "Could not export account data.",
        deleteConfirm:
          "Delete your account and stored data from OnlyGrades? This cannot be undone.",
        deleteSuccess: "Your account and stored data have been deleted.",
        deleteFailed: "Could not delete your account.",
        unlockPrompt: "Unlock premium to generate personalized program-level recommendations.",
        unlockButton: "Unlock Premium",
        plannerTitle: "Next Semester Planner",
        studyPlanTitle: "My study plan",
        studyPlanSubtitle:
          "Add courses you want to keep track of. You can remove them anytime.",
        studyPlanCount: "Saved courses: {{count}}",
        studyPlanLoading: "Loading study plan...",
        studyPlanEmpty: "No courses in your study plan yet.",
        studyPlanLoadFailed: "Could not load your study plan right now.",
        term: "Target semester",
        risk: "Risk profile",
        riskLow: "Low risk",
        riskBalanced: "Balanced",
        riskHigh: "Higher upside",
        passedCourses: "Completed course codes",
        passedPlaceholder: "TMA4100, TDT4100, ...",
        loadPassed: "Load saved courses",
        savePassed: "Save courses",
        maxCourses: "Number of recommendations",
        generate: "Generate recommendations",
        resultsTitle: "Recommendations",
        checkoutSuccess: "Payment completed. Refreshing access...",
        sessionExpired: "Session expired. Please sign in again.",
        loginSuccess: "Signed in successfully.",
        loginFailed: "Google sign-in failed.",
        loggedOut: "Signed out.",
        savedWithMissing: "Saved known courses. Unknown course codes:",
        savedPassed: "Saved completed courses.",
        saveFailed: "Failed to save completed courses.",
        checkoutFailed: "Failed to start checkout.",
        selectProgramFirst: "Select institution and study program first.",
        noResults: "No recommendations found with current filters.",
        planFailed: "Failed to generate recommendations.",
        programLevelLocked:
          "Program-level easiest/hardest rankings are premium. Showing university-level results.",
        programLevelComingSoon:
          "Program-level easiest/hardest rankings are coming soon. Showing university-level results for now.",
        comingSoonTitle: "Premium is coming soon",
        comingSoonBody:
          "Join the early signup list and we will notify you first when personalized program-level rankings are launched.",
        waitlistNameLabel: "Name (optional)",
        waitlistEmailLabel: "Email for early access",
        waitlistSubmit: "Join early signup",
        waitlistSubmitting: "Submitting...",
        waitlistSuccess:
          "Thanks! You are on the early signup list.",
        waitlistFailed:
          "Could not submit right now. Please try again in a moment.",
        waitlistEmailRequired: "Please enter an email address.",
        waitlistConsentPrefix: "I have read the",
        waitlistConsentSuffix:
          "and agree to receive non-binding launch updates by email.",
        waitlistConsentRequired:
          "Please accept the privacy terms to join the waitlist.",
      },
      privacy: {
        title: "Privacy Policy",
        lastUpdated: "Last updated",
        intro:
          "OnlyGrades processes only the data needed to run core features and premium services.",
        item1:
          "Data processed: Google account identifier, email, name/picture (if available), payment metadata, and optional completed course codes.",
        item2:
          "Purpose: account login, premium access control, payment handling, and personalized recommendations.",
        item3:
          "Legal basis: contract (service delivery), legitimate interests (security/operations), and consent for optional analytics.",
        item4:
          "Storage: account/auth/payment metadata is retained while your account is active or as required by law.",
        item5:
          "Processors: Google (sign-in), Stripe (billing), and hosting providers used to run the service.",
        item6:
          "Rights: you can request access, correction, export, or deletion of your personal data.",
        item7:
          "Cookies/analytics: optional analytics is disabled until you explicitly consent.",
        item8:
          "Contact us using the support email listed on this page for privacy requests.",
        accountTools:
          "Signed-in users can export or delete their account data from the Premium page.",
      },
      terms: {
        title: "Terms of Service",
        lastUpdated: "Last updated",
        intro:
          "OnlyGrades provides statistical guidance and planning support for course selection.",
        item1:
          "No guarantee: recommendations are statistical estimates, not official study advice or guaranteed outcomes.",
        item2:
          "Availability: we may update, change, or pause services while improving data quality or infrastructure.",
        item3:
          "Premium billing: paid features are unlocked after successful payment confirmation.",
        item4:
          "Acceptable use: users must not abuse, scrape, or attempt to disrupt the service.",
        item5:
          "Support and cancellation questions are handled through the support email listed on this page.",
      },
    },
    seo: {
      defaultDescription:
        "Find detailed grade statistics, fail rates, and trends for university courses in Norway.",
      home: {
        title: "OnlyGrades - grade statistics for courses",
        description:
          "Search course codes and explore historical grade averages, fail rates, and distributions.",
      },
      course: {
        unknownCode: "Course",
        title: "{{code}} grade statistics | OnlyGrades",
        description:
          "Historical grades, fail rates, and distributions for {{code}} across recent semesters.",
      },
      easiestHardest: {
        title: "Easiest and hardest courses | OnlyGrades",
        description:
          "Compare easiest and hardest courses by institution and study program.",
      },
      advanced: {
        title: "Advanced course analysis | OnlyGrades",
        description:
          "Risk/reward map, stability scores, and offering probability signals for course planning.",
      },
      premium: {
        title: "OnlyGrades Premium",
        description:
          "See what Premium includes: larger dashboards, advanced signals, and saved study planning.",
      },
      account: {
        title: "My account | OnlyGrades",
        description:
          "Manage account details, saved study plan, and privacy tools in one place.",
      },
      about: {
        title: "About OnlyGrades",
        description:
          "How OnlyGrades uses DBH data to help students make better course choices.",
      },
      privacy: {
        title: "Privacy policy | OnlyGrades",
        description:
          "Read how OnlyGrades handles personal data, analytics consent, and account information.",
      },
      terms: {
        title: "Terms of service | OnlyGrades",
        description:
          "Review the terms for using OnlyGrades and premium features.",
      },
    },
  },
  no: {
    title: "OnlyGrades",
    message: {
      home: "Hjem",
      premiumNav: "Premium",
      advancedNav: "Avansert",
      privacyNav: "Personvern",
      termsNav: "Vilkår",
      about: "Om",
      easiestHardestCourses: "Enkleste/Vanskeligste Fag",
      accountMenu: "Konto",
      logout: "Logg ut",
      aboutTitle: "Om OnlyGrades",
      aboutDescription1:
        "OnlyGrades er en omfattende plattform designet for å hjelpe studenter med å finne detaljerte karakterstatistikker for Fagene sine. Vårt mål er å gi verdifulle innsikter i Fagytelse, slik at studentene kan ta informerte beslutninger om utdanningen sin.",
      aboutDescription2:
        "Enten du leter etter de enkleste Fagene, detaljerte karakterfordelinger eller historiske ytelsesdata, har OnlyGrades det du trenger.",
      aboutDescription3:
        "Hvis du har spørsmål eller trenger ytterligere assistanse, vennligst gi oss beskjed på {{email}}.",
      aboutDescription3NoEmail:
        "Hvis du har spørsmål eller trenger ytterligere assistanse, vennligst kom tilbake snart for kontaktinformasjon.",
      aboutDataTitle: "Data og lisens",
      aboutDataDescription1:
        "OnlyGrades bruker aggregerte statistikkdata fra HK-dir sin DBH-tjeneste (Database for statistikk om høyere utdanning).",
      aboutDataDescription2:
        "Data fra DBH API publiseres under NLOD 2.0, som tillater kommersiell bruk når kildeangivelse og lisensvilkår følges.",
      aboutDataAttributionLabel: "Anbefalt kildeangivelse:",
      aboutDataAttributionQuote:
        "Data fra Database for statistikk om høyere utdanning (DBH), lisensiert under NLOD 2.0.",
      aboutDataRequirement1:
        "Kildeangivelse: DBH/HK-dir og NLOD bør vises der data presenteres eller eksporteres.",
      aboutDataRequirement2:
        "Dataintegritet: resultater skal ikke presenteres villedende eller fordreid.",
      aboutDataRequirement3:
        "Persondata: kun aggregerte/åpne data er i scope; personopplysninger krever eget behandlingsgrunnlag.",
      aboutDataSourcesTitle: "Offisielle kilder",
      aboutLinkDbhDocs: "DBH API-dokumentasjon (PDF)",
      aboutLinkDataset: "DBH API-datasett (data.norge.no)",
      aboutLinkNlod: "NLOD 2.0 lisens",
      aboutDataVerified: "Lisensstatus sist verifisert: {{date}}.",
      maintenance: {
        title: "Under bygging",
        description:
          "OnlyGrades er midlertidig utilgjengelig mens vi bygger opp backend på nytt.",
        contact: "Kontakt: {{email}}",
      },
      homeWelcome: "Velkommen til OnlyGrades",
      homeSubtitle: "Finn detaljerte karakterstatistikker for fagene dine.",
      chartHistorical: "Historisk Visning",
      chartDistribution: "Karakterfordeling",
      chartFailRates: "Historiske Strykprosent",
      excludeFails: "Ekskluder stryk fra gjennomsnitt",
      includeFails: "Inkluder stryk i gjennomsnitt",
      selectAverageOption: "Velg beregning for gjennomsnittskarakter",
      showFemaleStats: "Vis Kvinnelig Statistikk",
      hideFemaleStats: "Skjul Kvinnelig Statistikk",
      womenStats: "Kvinner",
      semesters: {
        winter: "Vinter",
        spring: "Vår",
        summer: "Sommer",
        fall: "Høst",
      },
      search: {
        placeholder: "Søk etter fag...",
        searchInstitution: "Søk etter institusjoner...",
        searchStudyProgram: "Søk etter studieprogrammer...",
        loadingCourses: "Laster emner...",
      },
      easiestHardestTitle: "Enkleste og Vanskeligste Fag",
      easiestCourses: "Enkleste Fag",
      hardestCourses: "Vanskeligste Fag",
      allRightsReserved: "Alle Rettigheter Forbeholdt",
      dataAttribution:
        "Inneholder data under NLOD fra HK-dir via Database for statistikk om høyere utdanning (DBH).",
      cookies: {
        bannerText:
          "Vi bruker nødvendig lagring for innlogging/økt og valgfri analyse kun etter samtykke.",
        learnMore: "Personverndetaljer",
        reject: "Kun nødvendige",
        accept: "Godta analyse",
        settings: "Cookie-innstillinger",
      },
      api: {
        loadingCourses: "Laster emnekatalog...",
        loadingCourseData: "Laster grafdata...",
        backendWakingUp:
          "Backend starter opp. Dette kan ta litt tid etter inaktivitet.",
        retry: "Prøv igjen",
        catalogUnavailable:
          "Emnekatalogen er midlertidig utilgjengelig mens backend starter.",
        catalogLoadFailed: "Kunne ikke laste emnekatalogen akkurat nå.",
        courseUnavailable: "Kunne ikke laste dette emnet ennå. Prøv igjen straks.",
        courseNotFound: "Fant ikke data for {{code}}.",
      },
      dashboard: {
        activeCharts: "Grafer: {{count}} / {{max}}",
        tierFree: "Gratis dashboard",
        tierPremium: "Premium dashboard",
        searchToAdd: "Søk og legg til emner for sammenligning side om side.",
        compareBuilderTitle: "Sammenlign i dashboard",
        compareBuilderBody:
          "Velg flere emner her, og åpne dem direkte i analyse-dashboardet.",
        addTopEasiest: "Legg til topp 3 enkleste",
        addTopHardest: "Legg til topp 3 vanskeligste",
        openSelected: "Åpne valgte ({{count}})",
        clearCourses: "Tøm dashboard",
        clearSelected: "Tøm valgte",
        selectedCount: "Valgte emner: {{count}}",
        addCourse: "Legg til",
        addToStudyPlan: "Legg til i studieplan",
        studyPlanSavedLabel: "I studieplan",
        studyPlanRemoveLabel: "Fjern fra plan",
        studyPlanCount: "Studieplan: {{count}}",
        studyPlanSaved: "{{code}} lagt til i studieplanen din.",
        studyPlanSavedDev:
          "{{code}} lagt til i studieplanen din (lokal utviklingsmodus).",
        studyPlanRemoved: "{{code}} fjernet fra studieplanen din.",
        studyPlanRemovedDev:
          "{{code}} fjernet fra studieplanen din (lokal utviklingsmodus).",
        studyPlanAlreadySaved: "{{code}} finnes allerede i studieplanen din.",
        studyPlanLoginRequired:
          "Logg inn på Premium for å lagre emner i studieplanen.",
        studyPlanUnavailable:
          "Studieplan er ikke tilgjengelig akkurat nå.",
        studyPlanFailed: "Kunne ikke oppdatere studieplanen nå.",
        inDashboardPickerTitle: "Premium emnevelger (legg direkte til i dashboard)",
        toolsTitle: "Dashboard-verktøy",
        toolsSubtitle: "Juster analysevalg, grafstil og eksport.",
        expandTools: "Vis verktøy",
        collapseTools: "Skjul verktøy",
        summarySmartPicks: "Smarte valg: {{count}} / {{total}}",
        summarySmartPicksHelp:
          "Emner i dashboardet ditt som vurderes som lav risiko og høy belønning.",
        summaryStability: "Snitt stabilitet",
        summaryStabilityHelp:
          "Hvor forutsigbare resultatene er over nyere semestre. Høyere betyr mer stabilt.",
        summaryOfferProbability: "Snitt tilbudssannsynlighet",
        summaryOfferProbabilityHelp:
          "Estimert sannsynlighet for at emnene tilbys i relevant semester, basert på de siste årene.",
        riskAxisLabel: "Risikoscore (lavere er bedre)",
        rewardAxisLabel: "Belønningsscore (høyere er bedre)",
        riskRewardMapSubtitle:
          "Hvert punkt er ett emne, skalert fra 0-100 på risiko og belønning.",
        riskRewardMapClickHint:
          "Klikk på et emnepunkt for å hoppe direkte til emnekortet under.",
        riskRewardMethodTitle: "Slik beregnes dette",
        riskRewardMethodReward:
          "Belønning = karakternivå (84%) + stabilitet (16%).",
        riskRewardMethodRisk:
          "Risiko = lav beståttandel (63%) + lav tilbudssannsynlighet (22%) + ustabilitet (15%).",
        riskRewardMethodQuadrant:
          "Smarte valg er emner med risiko <= 40 og belønning >= 60.",
        toolsSectionView: "Visning",
        toolsSectionStyle: "Stil",
        toolsSectionAdvanced: "Avansert",
        toolsSectionExport: "Eksport",
        viewOptions: "Analysevalg",
        filterBothSemestersOn: "Kun emner i vår+høst",
        filterBothSemestersOff: "Vis alle tilbudsmønstre",
        filterBothSemestersHelp:
          "Filtrer grafer og innsikt til emner som historisk tilbys både vår og høst.",
        programFocusTitle: "Institusjon/program-filter",
        programFocusHelp:
          "Velg institusjon først, deretter eventuelt studieprogram. Emnekort uten treff skjules.",
        programFocusInstitutionAll: "Alle institusjoner",
        programFocusStudyProgramAll: "Alle studieprogram",
        programFocusHiddenCount: "{{count}} emnekort er skjult av dette filteret.",
        programFocusEmpty:
          "Ingen åpne emnekort matcher dette institusjon/program-filteret.",
        filterBothSemestersHiddenCount:
          "{{count}} emner er skjult av dette filteret.",
        filterBothSemestersEmpty:
          "Ingen åpne emner matcher filteret. Slå det av for å se alle emner.",
        bulkChartMode: "Bruk graftype på alle åpne emner",
        setAllHistorical: "Alle historikk",
        setAllDistribution: "Alle fordeling",
        setAllFailRates: "Alle strykrate",
        visualPalette: "Grafpalett",
        paletteOnlygrades: "OnlyGrades",
        paletteSlate: "Slate",
        paletteWarm: "Varm",
        advancedTitle: "Avanserte signaler",
        advancedSubtitle:
          "Kompakt rangering fra emnene i dashboardet (risiko/belønning, tilgjengelighet, stabilitet).",
        advancedPremiumLocked:
          "Avanserte dashboardsignaler er premium.",
        noAdvancedSignals: "Legg til flere emner for å generere avanserte signaler.",
        riskRewardMapTitle: "Risiko/Belønning-kart",
        riskLow: "Lav risiko",
        riskHigh: "Høy risiko",
        rewardLow: "Lav belønning",
        rewardHigh: "Høy belønning",
        signalStability: "Stabilitet",
        signalOfferedBothSemesters: "Tilbys både vår og høst",
        exportTitle: "Eksport",
        exportSubtitle: "Eksporter gjeldende dashboard-data som CSV eller JSON.",
        exportCsv: "Eksporter CSV",
        exportJson: "Eksporter JSON",
        exportLocked: "Eksport er en premium-funksjon.",
        exportReadyCsv: "CSV-eksport er klar.",
        exportReadyJson: "JSON-eksport er klar.",
        saveDashboard: "Lagre dashboard",
        loadDashboard: "Last lagret",
        dashboardSaved: "Dashboard lagret.",
        dashboardLoaded: "Lagret dashboard lastet inn.",
        noSavedDashboard: "Fant ikke noe lagret dashboard.",
        dashboardLoadFailed: "Kunne ikke laste lagret dashboard.",
        removeCourse: "Fjern emne",
        removeCourseShort: "Fjern",
        duplicateCourse: "Emnet er allerede i dashboardet. Oppdatert med nyeste data.",
        fetchFailed: "Kunne ikke laste dette emnet nå.",
        noData: "Ingen grafdata for dette valget.",
        studyProgramFilter: "Studieprogram",
        studyProgramAll: "Alle studieprogram",
        studyPlanShortAdd: "Plan +",
        studyPlanShortRemove: "Plan -",
        chartType: "Graftype",
        limitReached: "Du har nådd dashboard-grensen ({{max}} emner).",
        premiumLocked:
          "Du kan sammenligne 2 emner gratis. Lås opp premium for 3-8 emner og avanserte dashboard-funksjoner.",
        unlockPremium: "Lås opp premium",
      },
      account: {
        title: "Min konto",
        subtitle:
          "Administrer innlogging, lagret studieplan og personvernverktøy på ett sted.",
        loginPrompt:
          "Logg inn med Google for å få tilgang til lagret studieplan og kontoverktøy.",
        devMockHint:
          "Utviklingsmodus: studieplan lagres lokalt i denne nettleseren når du ikke er innlogget.",
        devSignInFree: "Dev-innlogging (gratis)",
        devSignInPremium: "Dev-innlogging (premium)",
        devMockSession: "Dev mock-økt",
        devSwitchToFree: "Bytt til gratis",
        devSwitchToPremium: "Bytt til premium",
        devSignedIn: "Logget inn med lokal dev mock-konto.",
        devMockNoServer:
          "Dette er en lokal mock-økt. Dataeksport/sletting krever ekte innlogging + backend-auth.",
        unavailable:
          "Kontofunksjoner er ikke tilgjengelig akkurat nå.",
      },
      advanced: {
        title: "Avanserte analyser (beta)",
        subtitle:
          "Enkle forklaringer på analyser vi kan lansere med dagens data.",
        devNote:
          "Denne fanen er for utvikling. Alt her kan bygges med data vi allerede beregner.",
        controlsTitle: "Innstillinger for innsikt",
        generateInsights: "Generer innsikt",
        mapTitle: "Risiko/Belønning-kart",
        shortlistTitle: "Kortliste for neste semester",
        noInsightsYet: "Ingen innsikt ennå. Velg institusjon/program og generer.",
        noShortlistYet: "Ingen kortliste ennå. Generer innsikt først.",
        insightsFailed: "Kunne ikke generere innsikt.",
        highValueTitle: "Høy verdi nå",
        nextLevelTitle: "Neste nivå senere",
        metrics: {
          risk: "Risiko",
          reward: "Belønning",
          passRate: "Ståprosent",
          offerProbability: "Tilbudssannsynlighet",
        },
        quadrants: {
          smart_picks: "Smarte valg",
          safe_choices: "Trygge valg",
          high_risk_high_reward: "Høy risiko / høy oppside",
          review_carefully: "Vurder nøye",
        },
        reasonLabels: {
          high_pass_rate: "Høy ståprosent",
          stable: "Stabile utfall",
          offered_often: "Tilbys ofte",
          offered_both_semesters: "Går begge semestre",
          high_confidence: "Høy sikkerhet",
          balanced_profile: "Balansert profil",
        },
        items: {
          riskReward: {
            title: "Risiko/Belønning-kart",
            userText:
              "Finn emner som er lettere enn forventet uten for høy risiko.",
            dataText:
              "Kombinerer justert vanskelighetsgrad, ståprosent, tilbudssannsynlighet og stabilitet (SEM).",
          },
          confidenceRanking: {
            title: "Sikkerhetsjustert rangering",
            userText:
              "Se hvilke rangeringer som er robuste, og hvilke som er mer usikre.",
            dataText:
              "Viser konservativ score med nedre konfidensgrense (Adjusted_Difficulty_Lower_CL).",
          },
          trendArrows: {
            title: "Trendpiler",
            userText:
              "Se raskt om et emne blir lettere, vanskeligere, tryggere eller mer risikofylt over tid.",
            dataText:
              "Bruker utvikling i snittkarakter og ståprosent på de siste tilbudene.",
          },
          stabilityScore: {
            title: "Stabilitetsscore",
            userText: "Prioriter emner med mer forutsigbare utfall.",
            dataText:
              "Straffer høy variasjon (SEM) og lav historisk datamengde.",
          },
          offerProbability: {
            title: "Tilbudssannsynlighet",
            userText:
              "Unngå å planlegge rundt emner som sjelden går.",
            dataText:
              "Bygger på hvor ofte emnet tilbys i samme semester de siste årene.",
          },
          genderGap: {
            title: "Kjønnsforskjeller",
            userText:
              "Se hvor kvinner og totalgruppen presterer ulikt.",
            dataText:
              "Sammenligner average_grade_women_no_f og pass_rate_women mot totalen.",
          },
          anomalyAlerts: {
            title: "Anomalivarsler",
            userText:
              "Marker emner som plutselig har endret vanskelighetsgrad eller strykmønster.",
            dataText:
              "Finner avvik mot emnets egen historiske baseline.",
          },
          programHeatmap: {
            title: "Program-heatmap",
            userText: "Se tunge semestre på et øyeblikk.",
            dataText:
              "År/semester-heatmap med vanskelighets- og stryksignaler per program.",
          },
          paretoCourses: {
            title: "Pareto-kortliste",
            userText:
              "Vis emner med best balanse mellom lettere + stabile + sannsynlig tilbud.",
            dataText:
              "Flerkriterie-filter basert på vanskelighetsgrad, stabilitet og tilbudssannsynlighet.",
          },
          personalShortlist: {
            title: "Personlig kortliste",
            userText:
              "Lag en ryddigere anbefalingsliste fra fullførte emner og risikoprofil.",
            dataText:
              "Utvider dagens planner-score med sikkerhet og tilgjengelighet.",
          },
        },
      },
      premium: {
        title: "Premium Planlegger",
        subtitle:
          "Lag en personlig anbefaling basert på studieprogram, fullførte emner og risikoprofil.",
        disclaimer:
          "Anbefalinger er statistiske estimater, ikke offisiell studieveiledning eller garanterte resultater.",
        whatIncludedTitle: "Dette får du med Premium",
        whatIncludedBody:
          "Premium er laget for studenter som vil ta raskere og bedre valg på tvers av flere emner.",
        highlightPersonalized: "Personlig planlegging fra egen emnehistorikk",
        highlightCompare: "Sammenlign flere emner side om side i dashboard",
        highlightRiskMap: "Risiko/belønning-kart og avanserte signaler",
        highlightStudyPlan: "Lagret studieplan koblet til kontoen din",
        freeTierTitle: "Gratis",
        freeTierPrice: "0 kr/mnd",
        premiumTierTitle: "Premium",
        premiumTierPrice: "Betalt plan",
        freeFeatureSearch: "Søk og utforsk historiske karakterdata",
        freeFeatureCompare2: "Sammenlign opptil 2 emner i dashboard",
        freeFeatureBasicLists: "Enkleste/vanskeligste på institusjonsnivå",
        freeFeatureHistoryCharts: "Kjernetrender, fordeling og strykrate-grafer",
        premiumFeatureCompare8: "Sammenlign opptil 8 emner i ett dashboard",
        premiumFeatureAdvancedSignals:
          "Avanserte signaler: stabilitet, tilbudssannsynlighet, risiko/belønning",
        premiumFeaturePersonalPlanner:
          "Personlige anbefalinger på programnivå",
        premiumFeatureStudyPlan: "Studieplan lagret på brukerkonto",
        premiumFeatureExport: "Eksport og selvbetjent kontodata",
        premiumFeaturePriority: "Tidlig tilgang til nye premium-analyser",
        entryCta: "Se Premium",
        entryCopy: "Se hva Premium inneholder og lås opp når du vil.",
        signInToUnlock: "Logg inn for å låse opp Premium",
        signInRequiredCheckout: "Logg inn først for å starte betaling.",
        checkoutUnavailable:
          "Premium-betaling er ikke aktivert akkurat nå. Meld deg på tidliglisten.",
        openPlanner: "Åpne planlegger",
        loginPrompt: "Logg inn med Google for å lagre fullførte emner og bruke premium-planlegging.",
        planStatus: "Planstatus",
        refreshStatus: "Oppdater status",
        logout: "Logg ut",
        accountToolsTitle: "Personvernverktøy",
        exportData: "Last ned mine data",
        deleteAccount: "Slett kontoen min",
        exportSuccess: "Dataeksporten er klar.",
        exportFailed: "Kunne ikke eksportere kontodata.",
        deleteConfirm:
          "Slette kontoen din og lagrede data fra OnlyGrades? Dette kan ikke angres.",
        deleteSuccess: "Kontoen din og lagrede data er slettet.",
        deleteFailed: "Kunne ikke slette kontoen din.",
        unlockPrompt: "Lås opp premium for personlige anbefalinger på programnivå.",
        unlockButton: "Lås opp Premium",
        plannerTitle: "Neste semester-plan",
        studyPlanTitle: "Min studieplan",
        studyPlanSubtitle:
          "Legg til emner du vil følge med på. Du kan fjerne dem når som helst.",
        studyPlanCount: "Lagrede emner: {{count}}",
        studyPlanLoading: "Laster studieplan...",
        studyPlanEmpty: "Ingen emner i studieplanen enda.",
        studyPlanLoadFailed: "Kunne ikke laste studieplanen akkurat nå.",
        term: "Målsemester",
        risk: "Risikoprofil",
        riskLow: "Lav risiko",
        riskBalanced: "Balansert",
        riskHigh: "Høyere oppside",
        passedCourses: "Fullførte emnekoder",
        passedPlaceholder: "TMA4100, TDT4100, ...",
        loadPassed: "Last inn lagrede emner",
        savePassed: "Lagre emner",
        maxCourses: "Antall anbefalinger",
        generate: "Generer anbefalinger",
        resultsTitle: "Anbefalinger",
        checkoutSuccess: "Betaling fullført. Oppdaterer tilgang...",
        sessionExpired: "Økten er utløpt. Logg inn på nytt.",
        loginSuccess: "Innlogging fullført.",
        loginFailed: "Google-innlogging feilet.",
        loggedOut: "Logget ut.",
        savedWithMissing: "Lagret kjente emner. Ukjente emnekoder:",
        savedPassed: "Fullførte emner lagret.",
        saveFailed: "Kunne ikke lagre fullførte emner.",
        checkoutFailed: "Kunne ikke starte betaling.",
        selectProgramFirst: "Velg institusjon og studieprogram først.",
        noResults: "Fant ingen anbefalinger med valgte filtre.",
        planFailed: "Kunne ikke generere anbefalinger.",
        programLevelLocked:
          "Programnivå for enkleste/vanskeligste emner er premium. Viser resultater på institusjonsnivå.",
        programLevelComingSoon:
          "Programnivå for enkleste/vanskeligste emner kommer snart. Viser resultater på institusjonsnivå foreløpig.",
        comingSoonTitle: "Premium kommer snart",
        comingSoonBody:
          "Meld deg på tidligliste, så varsler vi deg først når personlige rangeringer på programnivå lanseres.",
        waitlistNameLabel: "Navn (valgfritt)",
        waitlistEmailLabel: "E-post for tidlig tilgang",
        waitlistSubmit: "Meld meg på",
        waitlistSubmitting: "Sender...",
        waitlistSuccess:
          "Takk! Du er lagt til på tidliglisten.",
        waitlistFailed:
          "Kunne ikke sende nå. Prøv igjen om litt.",
        waitlistEmailRequired: "Legg inn en e-postadresse.",
        waitlistConsentPrefix: "Jeg har lest",
        waitlistConsentSuffix:
          "og samtykker til å motta ikke-bindende lanseringsoppdateringer på e-post.",
        waitlistConsentRequired:
          "Du må godta personvernvilkårene for å melde deg på.",
      },
      privacy: {
        title: "Personvernerklæring",
        lastUpdated: "Sist oppdatert",
        intro:
          "OnlyGrades behandler kun data som er nødvendig for å levere kjernetjenesten og premium-funksjoner.",
        item1:
          "Data som behandles: Google-identifikator, e-post, navn/bilde (hvis tilgjengelig), betalingsmetadata og valgfritt fullførte emnekoder.",
        item2:
          "Formål: innlogging, tilgangsstyring for premium, betalingshåndtering og personlige anbefalinger.",
        item3:
          "Behandlingsgrunnlag: avtale (levering av tjenesten), berettiget interesse (sikkerhet/drift) og samtykke for valgfri analyse.",
        item4:
          "Lagring: konto-/auth-/betalingsmetadata lagres så lenge kontoen er aktiv eller når lovkrav krever det.",
        item5:
          "Databehandlere: Google (innlogging), Stripe (betaling) og hosting-leverandører brukt for å drifte tjenesten.",
        item6:
          "Rettigheter: du kan be om innsyn, retting, eksport eller sletting av personopplysninger.",
        item7:
          "Cookies/analyse: valgfri analyse er deaktivert til du gir eksplisitt samtykke.",
        item8:
          "Kontakt oss via support-epost på denne siden for personvernhenvendelser.",
        accountTools:
          "Innloggede brukere kan eksportere eller slette kontodata fra Premium-siden.",
      },
      terms: {
        title: "Bruksvilkår",
        lastUpdated: "Sist oppdatert",
        intro:
          "OnlyGrades leverer statistisk beslutningsstøtte og planleggingshjelp for emnevalg.",
        item1:
          "Ingen garanti: anbefalinger er statistiske estimater, ikke offisiell studieveiledning eller garanterte resultater.",
        item2:
          "Tilgjengelighet: vi kan oppdatere, endre eller pause tjenesten for å forbedre datakvalitet og infrastruktur.",
        item3:
          "Premium-betaling: betalte funksjoner åpnes etter bekreftet betaling.",
        item4:
          "Akseptabel bruk: brukere må ikke misbruke, scrape eller forsøke å forstyrre tjenesten.",
        item5:
          "Support- og oppsigelsesspørsmål håndteres via support-epost på denne siden.",
      },
    },
    seo: {
      defaultDescription:
        "Finn detaljerte karakterstatistikker, strykrater og trender for emner i høyere utdanning i Norge.",
      home: {
        title: "OnlyGrades - karakterstatistikk for emner",
        description:
          "Søk på emnekoder og utforsk historiske snittkarakterer, strykrater og fordelinger.",
      },
      course: {
        unknownCode: "Emne",
        title: "{{code}} karakterstatistikk | OnlyGrades",
        description:
          "Historiske karakterer, strykrater og fordelinger for {{code}} på tvers av nyere semestre.",
      },
      easiestHardest: {
        title: "Enkleste og vanskeligste emner | OnlyGrades",
        description:
          "Sammenlign enkleste og vanskeligste emner etter institusjon og studieprogram.",
      },
      advanced: {
        title: "Avansert emneanalyse | OnlyGrades",
        description:
          "Risiko/belønning-kart, stabilitetsscore og tilbudssannsynlighet for planlegging av emner.",
      },
      premium: {
        title: "OnlyGrades Premium",
        description:
          "Se hva Premium inkluderer: større dashboard, avanserte signaler og lagret studieplan.",
      },
      account: {
        title: "Min konto | OnlyGrades",
        description:
          "Administrer kontodetaljer, lagret studieplan og personvernverktøy på ett sted.",
      },
      about: {
        title: "Om OnlyGrades",
        description:
          "Slik bruker OnlyGrades DBH-data for å hjelpe studenter med bedre emnevalg.",
      },
      privacy: {
        title: "Personvern | OnlyGrades",
        description:
          "Les hvordan OnlyGrades håndterer personopplysninger, analysesamtykke og kontodata.",
      },
      terms: {
        title: "Bruksvilkår | OnlyGrades",
        description:
          "Se vilkårene for bruk av OnlyGrades og premium-funksjoner.",
      },
    },
  },
};
